<template>
  <MainLayout :show-chrome="showAppShell">
    <V4GlobalBreadcrumbs v-if="showAppShell && false" />
    <router-view />
    <NotificationDialog
      v-if="notification.show"
      :content="notification.message"
      :icon="notification.icon"
      @close="notification.show = false"
    />
    <DownloadBanner
      v-model="banner"
      :handleCloseBanner="handleCloseBanner"
      :link="appleStore"
    />
    <NotificationComponent />
    <template id="loading-full-screen"></template>
  </MainLayout>
  <div id="modal_dialog_teleport"></div>
</template>
<script setup>
import {
  computed,
  inject,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import Echo from "laravel-echo";
import { useAuth } from "@websanova/vue-auth/src/v3.js";
import { storeToRefs } from "pinia";
import DownloadBanner from "./components/banner/DownloadBanner.vue";
import { isMobile } from "vue-device-detect";
import { useRoute } from "vue-router";
import { usePusherStore } from "@/stores/pusher";
import { useAuthStore } from "@/stores";
import { useDialogStore } from "@/stores/useDialogStore";
import MainLayout from "./layout/MainLayout.vue";
import NotificationComponent from "./components/partials/NotificationComponent.vue";
import V4GlobalBreadcrumbs from "./components/layout/v4/V4GlobalBreadcrumbs.vue";
import NotificationDialog from "@/components/partials/NotificationDialog";
import eventBus from "./plugins/eventBus";
window.Pusher = require("pusher-js");

const auth = useAuth();
const authStore = useAuthStore();
const dialogStore = useDialogStore();
const { token, user } = storeToRefs(authStore);
const { isDialogVisible } = storeToRefs(dialogStore);

const setHelpWidgetVisible = (visible) => {
  document.body.classList.toggle("help-widget-hidden", !visible);

  if (typeof window.zE !== "function") {
    return;
  }

  try {
    window.zE("messenger", visible ? "show" : "hide");
  } catch (e) {
    // Zendesk Messaging may not be configured
  }

  try {
    window.zE("webWidget", visible ? "show" : "hide");
  } catch (e) {
    // Classic Zendesk widget may not be configured
  }
};

watch(
  isDialogVisible,
  (visible) => {
    setHelpWidgetVisible(!visible);
  },
  { immediate: true }
);
const pusherConfig = usePusherStore();
const axios = inject("axios");
const pusherKey = ref("");
const banner = ref(false);
const appleStore = ref("");
const route = useRoute();
const notification = ref({
  message: "",
  show: false,
  icon: true,
});

const publicRouteNames = [
  "login",
  "forgot-password",
  "password-reset",
  "pre.login",
];

const publicPaths = [
  "/login",
  "/pre-login",
  "/forgot-password",
  "/password-reset",
];

const isAuthenticated = computed(() =>
  Boolean(auth.check() || (token.value && user.value))
);

const isPublicRoute = computed(() => {
  if (publicRouteNames.includes(route.name)) {
    return true;
  }

  const routePath = (route.path || "").replace(/\/+$/, "") || "/";
  const windowPath = (window.location.pathname || "/").replace(/\/+$/, "") || "/";

  return publicPaths.includes(routePath) || publicPaths.includes(windowPath);
});

const showAppShell = computed(
  () => isAuthenticated.value && !isPublicRoute.value
);

const showBannerForMobile = () => {
  const hasViewBanner = localStorage.getItem("has_view_banner");

  if (isMobile && !hasViewBanner && !window.MSStream) {
    banner.value = true;
  }
};

const handleCloseBanner = () => {
  localStorage.setItem("has_view_banner", 1);
  banner.value = false;
};

const fetchAuthUser = async () => {
  try {
    await authStore.ensureAuth();
    auth.user(user.value);
  } catch (error) {
    console.error(error);
  }
};

onBeforeMount(async () => {
  if (isPublicRoute.value) {
    return;
  }

  await fetchAuthUser();
});

onMounted(async () => {
  showBannerForMobile();

  eventBus.$on("NOTIFICATION_DIALOG", (data) => {
    notification.value = {
      show: true,
      message: data.message,
      icon: data.icon,
    };
  });

  await setEnv();
  setupPusher();

  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});

// A tab backgrounded through sleep/idle can miss its SSO logout event or
// come back with a revoked token; revalidate as soon as it's visible again
// instead of waiting for the next user action to surface a cascade of 401s.
function handleVisibilityChange() {
  if (document.visibilityState !== "visible") {
    return;
  }

  if (!isAuthenticated.value) {
    return;
  }

  authStore.getAuth();
}

const setEnv = async () => {
  await axios.get("/api/get-env").then(({ data }) => {
    pusherConfig.setPusher(data);
    pusherKey.value = {
      pusher_cluster: data.pusher_cluster,
      pusher_key: data.pusher_key,
    };
    appleStore.value = data.apple_store;
  });
};

function setupPusher() {
  window.Echo = new Echo({
    broadcaster: "pusher",
    key: pusherKey.value.pusher_key,
    cluster: pusherKey.value.pusher_cluster,
    forceTLS: true,
    authEndpoint: "/broadcasting/auth",
    encrypted: true,
    auth: {
      headers: {
        Authorization: `Bearer ${auth.token()}`,
      },
    },
  });

  ssoLogoutListener();
}

function logout() {
  const finish = (url) => {
    auth.logout();
    authStore.$patch({ user: null, token: null });
    localStorage.removeItem("nextUrl");
    window.location.href = url || "/login";
  };

  axios
    .get("/api/okta/logout")
    .then(({ data }) => finish(data))
    .catch(() => finish("/login"));
}

function goBack() {
  const params = {};
  if (route.query.user_central_id) {
    params.user_central_id = route.query.user_central_id;
  }

  axios
    .get("/api/okta/get-logout-link", { params })
    .then(({ data }) => {
      window.location.href = data;
    })
    .catch(() => {
      window.location.href = "/login";
    });
}

function ssoLogoutListener() {
  window.Echo.channel("sso-logout").listen("SSOLogout", (e) => {
    let centralId = auth.user()
      ? auth.user().central_id
      : route.query.user_central_id;

    if (centralId != e.user_central_id) {
      return;
    }

    if (route.query.user_central_id) {
      goBack();
      return;
    }

    logout();
  });
}
</script>

<style>
:root {
  --el-color-primary: #939495 !important;
  --el-button--primary: #939495 !important;
}

.el-button--primary:hover {
  background-color: #939495 !important;
}
::-webkit-scrollbar {
  /* width: 8px; */
}
::-webkit-scrollbar-track {
  background: #eaecf0;
  border-radius: 4px;
  margin: 10px 0;
}
::-webkit-scrollbar-thumb {
  background: #667085;
  /* border-radius: 4px; */
}
::-webkit-scrollbar-thumb:hover {
  background: #667085;
}
.c2::-webkit-scrollbar-track {
  background-color: "#101828";
}
.dp__pm_am_button {
  color: black !important;
}
.el-select-v2__wrapper {
  padding: 5px !important;
}
.el-dialog {
  border-radius: 20px !important;
}
.el-date-editor.el-input,
.el-date-editor.el-input__wrapper {
  width: 100% !important;
}
.el-dialog__body {
  padding: 0 !important;
}
.el-range-editor.el-input__wrapper {
  padding: 5px !important;
  width: 100% !important;
}
.el-select-v2__tags-text {
  max-width: 150px !important;
}
.el-select-v2__selection {
  width: 95% !important;
}

.el-popover.el-popper {
  z-index: 1 !important;
}

.el-popover.el-popper.v4-notification-popover,
.el-popover.el-popper.v4-apps-popover {
  z-index: 50 !important;
}

.is-pure {
  z-index: 4003 !important;
}

#si_frame {
  height: 100% !important;
}

#si-wrapper .silc-btn {
  bottom: 10px !important;
  height: 60px !important;
  width: 60px !important;
  right: 140px !important;
}

body.help-widget-hidden iframe#launcher,
body.help-widget-hidden iframe#webWidget,
body.help-widget-hidden iframe[title*="essage"],
body.help-widget-hidden iframe[title*="Help"],
body.help-widget-hidden iframe[title*="launch"],
body.help-widget-hidden #si-wrapper,
body.help-widget-hidden #si-wrapper .silc-btn,
body.help-widget-hidden [data-product="web_widget"] {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>
